import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Skeleton } from "@/components/ui/skeleton"
import { CountersCard } from "@/features/settings/CountersCard"
import { useCompany } from "@/features/settings/use-company"
import { ApiError } from "@/lib/api-error"

/**
 * `/invoices/numbers` — §6.1's counter override, on a screen of its own.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS SCREEN EXISTS AT ALL, when the same card is on Settings.
 *
 * `paths.ts` has said since the route was written that this is "ITS OWN ROUTE,
 * GATED ON `invoice.set_counter`, BECAUSE THE OWNER HOLDS THAT CAPABILITY AND
 * NOT `settings.write`". The route table said something else: `/invoices/numbers`
 * rendered `<Navigate to="/settings">`, and `/settings` is guarded by
 * `settings.write`. So the palette offered the OWNER a row he holds the
 * capability for, and selecting it bounced him — `invoice.view` passed, the
 * redirect fired, `settings.write` failed, Home. No message, nothing to act on,
 * indistinguishable from the app ignoring the tap. The ADMIN never saw it
 * because he holds both, which is why it survived manual testing.
 *
 * Under `/invoices/` rather than `/settings/` because that is what it is to him:
 * the number the next bill will get.
 *
 * THE COMPANY ROW IS FETCHED HERE, and the OWNER may fetch it: `GET /company` is
 * gated on `settings.view`, which every authenticated role holds — only
 * `settings.write` is ADMIN-only (§2.1). The card needs it to show which number
 * the app is on now, which is the whole reason he opened this.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function CountersScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const company = useCompany()

  return (
    <div className="flex w-full flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t("counters.title")}</h1>
        <p className="text-muted-foreground text-sm">{t("counters.screenHelp")}</p>
      </header>

      {/*
        * ═══════════════════════════════════════════════════════════════════════
        * DATA IS TESTED BEFORE ERROR, AND THAT ORDER IS THE WHOLE POINT.
        *
        * It read `isPending → isError → data`, so an errored REFETCH — the query
        * still holding perfectly good data — swapped `<CountersCard>` out for a
        * red box. That unmounted the card, and the server's warnings live in its
        * `React.useState` (CountersCard.tsx), above no error boundary and in no
        * cache. They were gone permanently.
        *
        * The scenario is not hypothetical, it is the one CountersCard's own
        * comment gives as the reason those warnings are inline rather than a
        * toast: the owner reads "invoice numbers 60 to 66 will be issued a
        * SECOND time", switches to WhatsApp to tell the CA, and comes back.
        * `refetchOnWindowFocus` is on for every query (lib/query-client.ts), and
        * the mutation's own `onSuccess` invalidates everything (use-company.ts),
        * so a refetch on workshop wi-fi is the ordinary case, not the edge.
        *
        * What he saw afterwards was worse than losing the warnings: red text
        * reading "Something went wrong" where the form had been, which reads as
        * THE SAVE HAVING FAILED. It succeeded. Nothing on screen said so.
        *
        * So: if there is data, the card renders — and a failed refresh is a
        * banner ABOVE it saying the figures may be stale, which is what actually
        * went wrong. The destructive full-screen error is kept for the only case
        * it describes truthfully: an error with nothing to show.
        * ═══════════════════════════════════════════════════════════════════════
        */}
      {company.isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : company.data !== undefined ? (
        <>
          {company.isError ? (
            <Alert>
              <AlertTitle>{t("counters.refreshFailedTitle")}</AlertTitle>
              <AlertDescription>{t("counters.refreshFailedHelp")}</AlertDescription>
            </Alert>
          ) : null}
          <CountersCard company={company.data} />
        </>
      ) : company.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.somethingWentWrong")}</AlertTitle>
          <AlertDescription>
            {company.error instanceof ApiError ? company.error.message : String(company.error)}
          </AlertDescription>
        </Alert>
      ) : (
        // Settled but empty — an API answering an unexpected envelope. A crash
        // here would take the whole screen; a skeleton says "still nothing".
        <Skeleton className="h-96 w-full" />
      )}
    </div>
  )
}
